import { getKnownRouteSegmentMetadata } from '@/layouts/constants';
import { getPortalAppDefinition, navigationConstants } from '@/shared/constants';
import type { BreadcrumbItem, PortalAppDefinition } from '@/shared/types';
import { parsePathnameSegments } from '@/shared/utils/path';

interface BreadcrumbOverride {
  label?: string;
  hideIntermediateSegments?: boolean;
}

const toTitleCaseLabel = (segment: string) =>
  segment
    .split('-')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

const getRouteSegmentLabel = (segment: string): string =>
  getKnownRouteSegmentMetadata(segment)?.label ?? toTitleCaseLabel(segment);

const getRouteSegmentIcon = (segment: string) => getKnownRouteSegmentMetadata(segment)?.icon;

const createAppBreadcrumb = (appDefinition: PortalAppDefinition, isCurrent: boolean): BreadcrumbItem => ({
  key: appDefinition.id,
  label: appDefinition.label,
  path: appDefinition.path,
  icon: appDefinition.icon,
  isCurrent,
});

const createRouteSegmentBreadcrumb = (
  segment: string,
  index: number,
  path: string,
  isCurrent: boolean,
): BreadcrumbItem => ({
  key: `${String(index)}-${segment}`,
  label: getRouteSegmentLabel(segment),
  path,
  icon: getRouteSegmentIcon(segment),
  isCurrent,
});

export const buildBreadcrumbs = (pathname: string, override: BreadcrumbOverride | null = null): BreadcrumbItem[] => {
  const segments = parsePathnameSegments(pathname);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      ...navigationConstants.appHomeBreadcrumb,
      isCurrent: segments.length === 0,
    },
  ];

  if (segments.length === 0) {
    return breadcrumbs;
  }

  let runningPath = '';
  let startIndex = 0;
  const appDefinition = getPortalAppDefinition(segments[0]);

  if (appDefinition) {
    breadcrumbs.push(createAppBreadcrumb(appDefinition, segments.length === 1));
    runningPath = appDefinition.path;
    startIndex = 1;
  }

  for (let index = startIndex; index < segments.length; index += 1) {
    const segment = segments[index];
    const isLastSegment = index === segments.length - 1;
    runningPath = `${runningPath}/${segment}`.replaceAll(/\/+/g, '/');

    // When hiding intermediate segments, collapse all remaining segments into a single breadcrumb
    if (override?.hideIntermediateSegments && override.label) {
      breadcrumbs.push(createRouteSegmentBreadcrumb(segment, index, runningPath, true));
      breadcrumbs[breadcrumbs.length - 1].label = override.label;
      break;
    }

    const breadcrumb = createRouteSegmentBreadcrumb(segment, index, runningPath, isLastSegment);

    // Apply custom label override to the last segment
    if (isLastSegment && override?.label) {
      breadcrumb.label = override.label;
    }

    breadcrumbs.push(breadcrumb);
  }

  return breadcrumbs;
};
